/**
 * Saved views (design §1): "Save view" stores the current filters with a name; "Share with team" and "Pin to Reports
 * home" are toggles. My views and views shared with me appear in this menu; pinned ones also on the Reports home.
 * A view I own can be emailed to me daily or weekly (`schedule`); someone else's shared view has no schedule control.
 */
import {
  Button,
  Dialog,
  EmptyState,
  Input,
  Menu,
  MenuContent,
  MenuItem,
  MenuLabel,
  MenuSeparator,
  MenuTrigger,
  Switch,
  toast,
} from '@patchbay/ui'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Bookmark, ChevronDown, Pin, Settings2, Trash2, Users } from 'lucide-react'
import * as React from 'react'
import { describeError } from '../../lib/api/client'
import type { ReportFilters, SavedViewOut, SavedViewPatch, ViewSchedule } from '../../lib/api/generated'
import { useMe } from '../auth/session'
import { createView, deleteView, type NativeReportKey, patchView, viewsQuery } from './api'
import { DELIVERY_COPY, ScheduleBadge, ScheduleSelect } from './schedule'
import { QueryError } from './shared'

function ViewToggles({ view, onPatch, busy }: { view: SavedViewOut; onPatch: (patch: SavedViewPatch) => void; busy: boolean }) {
  return (
    <span className="flex items-center gap-4">
      <label className="flex items-center gap-1.5 text-xs text-muted-foreground">
        <Switch
          aria-label={`Share ${view.name} with the team`}
          checked={Boolean(view.shared)}
          disabled={busy}
          onCheckedChange={(shared) => onPatch({ shared })}
        />
        Shared
      </label>
      <label className="flex items-center gap-1.5 text-xs text-muted-foreground">
        <Switch
          aria-label={`Pin ${view.name} to Reports home`}
          checked={Boolean(view.pinned)}
          disabled={busy}
          onCheckedChange={(pinned) => onPatch({ pinned })}
        />
        Pinned
      </label>
    </span>
  )
}

export function SaveViewMenu({
  report,
  filters,
  onApply,
}: {
  report: NativeReportKey
  /** The current filter bar as the API's filters (what a new view stores). */
  filters: ReportFilters
  onApply: (view: SavedViewOut) => void
}) {
  const me = useMe()
  const queryClient = useQueryClient()
  const views = useQuery(viewsQuery(filters.brand ?? undefined))
  const [saveOpen, setSaveOpen] = React.useState(false)
  const [manageOpen, setManageOpen] = React.useState(false)
  const [name, setName] = React.useState('')
  const [shared, setShared] = React.useState(false)
  const [pinned, setPinned] = React.useState(false)
  const [schedule, setSchedule] = React.useState<ViewSchedule | null>(null)
  const invalidate = () => queryClient.invalidateQueries({ queryKey: viewsQuery(filters.brand ?? undefined).queryKey })

  const create = useMutation({
    mutationFn: () => createView({ name: name.trim(), report, filters, shared, pinned, schedule }),
    onSuccess: (view) => {
      toast.success(`Saved "${view.name}"`)
      setSaveOpen(false)
      void invalidate()
    },
  })
  // Every save starts fresh: no name, private, unpinned, no leftover error (the last save's toggles never carry over).
  const openSave = () => {
    setName('')
    setShared(false)
    setPinned(false)
    setSchedule(null)
    create.reset()
    setSaveOpen(true)
  }
  const patch = useMutation({
    mutationFn: ({ id, body }: { id: string; body: SavedViewPatch }) => patchView(id, body),
    onSuccess: () => void invalidate(),
    onError: (err) => toast.error(`View unchanged: ${describeError(err)}`),
  })
  const remove = useMutation({
    mutationFn: (id: string) => deleteView(id),
    onSuccess: () => void invalidate(),
    onError: (err) => toast.error(`View not deleted: ${describeError(err)}`),
  })

  const forReport = (views.data ?? []).filter((v) => v.report === report)
  const mine = forReport.filter((v) => v.owner.id === me?.id)
  const sharedWithMe = forReport.filter((v) => v.owner.id !== me?.id)

  return (
    <>
      <Menu>
        <MenuTrigger render={<Button variant="outline" size="sm" />}>
          <Bookmark className="size-4" />
          Save view
          <ChevronDown className="size-4" />
        </MenuTrigger>
        <MenuContent className="w-72">
          <MenuItem onClick={openSave}>
            <Bookmark />
            Save current view…
          </MenuItem>
          <MenuItem onClick={() => setManageOpen(true)} disabled={!views.data}>
            <Settings2 />
            Manage views…
          </MenuItem>
          <MenuSeparator />
          {views.isError ? (
            <MenuLabel>Saved views could not load</MenuLabel>
          ) : views.isPending ? (
            <MenuLabel>Loading saved views…</MenuLabel>
          ) : (
            <>
              <MenuLabel>My views</MenuLabel>
              {mine.length ? (
                mine.map((v) => (
                  <MenuItem key={v.id} onClick={() => onApply(v)}>
                    {v.pinned ? <Pin /> : <Bookmark />}
                    <span className="truncate">{v.name}</span>
                    <ScheduleBadge schedule={v.schedule} className="ml-auto" />
                  </MenuItem>
                ))
              ) : (
                <MenuLabel className="font-normal">None saved for this report yet</MenuLabel>
              )}
              {sharedWithMe.length ? (
                <>
                  <MenuLabel>Shared with me</MenuLabel>
                  {sharedWithMe.map((v) => (
                    <MenuItem key={v.id} onClick={() => onApply(v)}>
                      <Users />
                      <span className="truncate">{v.name}</span>
                      <span className="ml-auto truncate text-xs text-muted-foreground">{v.owner.name}</span>
                    </MenuItem>
                  ))}
                </>
              ) : null}
            </>
          )}
        </MenuContent>
      </Menu>

      <Dialog
        open={saveOpen}
        onOpenChange={setSaveOpen}
        title="Save this view"
        description="Stores the current filters under a name. A rolling range like Last 4 weeks keeps rolling forward."
        footer={
          <div className="flex justify-end gap-2">
            <Button variant="ghost" onClick={() => setSaveOpen(false)}>
              Cancel
            </Button>
            <Button
              disabled={!name.trim() || create.isPending}
              pending={create.isPending}
              disabledReason={create.isPending ? 'Saving' : 'Name the view first'}
              onClick={() => create.mutate()}
            >
              Save view
            </Button>
          </div>
        }
      >
        <form
          className="space-y-3"
          onSubmit={(e) => {
            e.preventDefault()
            if (name.trim()) create.mutate()
          }}
        >
          <label className="block space-y-1 text-sm font-medium">
            Name
            <Input
              value={name}
              maxLength={80}
              autoFocus
              onChange={(e) => setName(e.currentTarget.value)}
              placeholder="e.g. High priority, last 4 weeks"
            />
          </label>
          <label className="flex items-center gap-2 text-sm">
            <Switch aria-label="Share with team" checked={shared} onCheckedChange={setShared} />
            Share with team
          </label>
          <label className="flex items-center gap-2 text-sm">
            <Switch aria-label="Pin to Reports home" checked={pinned} onCheckedChange={setPinned} />
            Pin to Reports home
          </label>
          <div className="space-y-1">
            <ScheduleSelect schedule={schedule} onChange={setSchedule} />
            <p className="text-xs text-muted-foreground">{DELIVERY_COPY}</p>
          </div>
          {create.isError ? <p className="text-sm text-destructive">Not saved: {describeError(create.error)}</p> : null}
        </form>
      </Dialog>

      <Dialog
        open={manageOpen}
        onOpenChange={setManageOpen}
        title="Saved views"
        description="Your views for this report. Views others share appear in the menu."
      >
        {views.isError ? (
          <QueryError error={views.error} what="Saved views" onRetry={() => void views.refetch()} />
        ) : mine.length === 0 ? (
          <EmptyState title="No saved views for this report yet." detail="Use Save current view to keep a set of filters." />
        ) : (
          <div className="space-y-2">
            <ul className="divide-y divide-border">
              {mine.map((v) => (
                <li key={v.id} className="flex flex-wrap items-center gap-3 py-2.5">
                  <button
                    type="button"
                    className="min-w-0 flex-1 cursor-pointer truncate text-left font-medium text-primary hover:underline"
                    onClick={() => {
                      setManageOpen(false)
                      onApply(v)
                    }}
                  >
                    {v.name}
                  </button>
                  <ViewToggles view={v} busy={patch.isPending} onPatch={(body) => patch.mutate({ id: v.id, body })} />
                  <ScheduleSelect
                    label={`Email me ${v.name}`}
                    schedule={v.schedule}
                    disabled={patch.isPending}
                    onChange={(next) => patch.mutate({ id: v.id, body: { schedule: next } })}
                  />
                  <Button
                    variant="ghost"
                    size="icon"
                    aria-label={`Delete ${v.name}`}
                    disabled={remove.isPending}
                    disabledReason="Deleting"
                    onClick={() => remove.mutate(v.id)}
                  >
                    <Trash2 />
                  </Button>
                </li>
              ))}
            </ul>
            <p className="text-xs text-muted-foreground">{DELIVERY_COPY}</p>
          </div>
        )}
      </Dialog>
    </>
  )
}
